import { flexRender, useReactTable } from '@tanstack/react-table';
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/table-core';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { ReactComponent as Star } from 'assets/yellow-star.svg';
import EmptyTable from 'components/EmptyTable';
import TableHeader from 'components/TableHeader';
import { useAuth } from 'contexts/AuthProvider';
import { useToast } from 'contexts/ToastProvider';
import { User } from 'entities/user.entity';
import { Dispatch, Ref, SetStateAction, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { containsFilter, getRoleText, mapMemberStatusColor } from 'utils/helper';
import { Role } from '..';

interface MembersType {
  globalFilter: string;
  setGlobalFilter: Dispatch<SetStateAction<string>>;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;
  setUserData: Dispatch<SetStateAction<User>>;
}

export interface MerbersRefType {
  getMembers: () => void;
}

const Members = ({ globalFilter, setGlobalFilter, setOpenDrawer, setUserData }: MembersType, ref: Ref<MerbersRefType>) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { getDeviceMembers } = useAuth();
  const [members, setMembers] = useState<User[]>([]);
  const [tableFilterResultLength, setTableFilterResultLength] = useState(0);

  useEffect(() => {
    setOpenDrawer(false);
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle<any, MerbersRefType>(
    ref,
    () => ({
      getMembers: () => getMembers(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getMembers = async () => {
    try {
      const { data } = await getDeviceMembers();
      setMembers(data.filter((d) => d.role !== 'root'));
    } catch (error) {
      addToast({
        title: t('toast.getTableDataFailed'),
        message: '',
        color: 'bg-toast-error-50',
        icon: ErrorCircle,
        text: 'text-toast-error-800',
        svg: 'toastIcon-error',
      });
    }
  };

  const MembersTable = () => {
    const onClickEdit = useCallback((data: User) => {
      setOpenDrawer(true);
      setUserData(data);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns = useMemo<ColumnDef<User, any>[]>(
      () => [
        {
          accessorKey: 'name',
          header: t('profile.tableName'),
          className: 'uppercase',
          cell: (props) => (
            <div className="flex flex-nowrap justify-center items-center">
              <span>{props.getValue() === '' ? '--' : props.getValue()}</span>
              {props.row.original.role === Role.Owner && <Star className="ml-2" />}
            </div>
          ),
        },
        {
          accessorKey: 'email',
          header: t('profile.tableEmail'),
          className: 'uppercase',
        },
        {
          accessorKey: 'status',
          header: t('profile.tableStatus'),
          className: 'uppercase',
          cell: (props) => <span className={`capitalize ${mapMemberStatusColor(props.getValue())}`}>{props.getValue()}</span>,
        },
        {
          accessorKey: 'role',
          header: '--',
          className: 'uppercase',
          cell: (props) => <span className="capitalize">{getRoleText(props.getValue())}</span>,
        },
        {
          accessorKey: 'edit',
          header: '',
          enableSorting: false,
          className: 'uppercase',
          cell: (props) =>
            props.row.original.role !== Role.Owner && (
              <span className="text-blue-500 cursor-pointer" onClick={() => onClickEdit(props.row.original)}>
                {t('profile.tableEdit')}
              </span>
            ),
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onClickEdit]
    );

    const table = useReactTable({
      data: members,
      columns,
      filterFns: { contains: containsFilter },
      state: { globalFilter },
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: containsFilter,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    useEffect(() => {
      setTableFilterResultLength(table.getPrePaginationRowModel().rows.length);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="table-overflow">
        <table className="table w-full text-center">
          <TableHeader table={table} />
          <tbody>
            {tableFilterResultLength > 0 ? (
              table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="hover">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                );
              })
            ) : (
              // <EmptyTable columnLength={5} />
              // TODO: 隱藏 Edit 按鈕，暫時不給修改 role
              <EmptyTable columnLength={4} />
            )}
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div className="card-body setting">
      <div className="pt-2 px-4">
        <MembersTable />
        <div className="h-2" />
        {tableFilterResultLength > 0 ? (
          <span className="text-xs text-gray-500">
            {t('component.tablePagination.rowLengthShowing')} {'1'} {t('component.tablePagination.rowLengthTo')} {tableFilterResultLength}{' '}
            {t('component.tablePagination.rowLengthOf')} {tableFilterResultLength}
            {tableFilterResultLength === 1 ? t('component.tablePagination.rowLengthResult') : t('component.tablePagination.rowLengthResults')}
          </span>
        ) : (
          <span className="text-xs text-gray-500">{t('component.tablePagination.rowLengthNoData')}</span>
        )}
      </div>
    </div>
  );
};

export default forwardRef(Members);
